// The parcel once an order is sent: its tracking number, asked for when the
// order is shipped (TrackingForm), and on the Delivery tab what the courier
// last said of it and what happens next (TrackingPanel) — the courier finishes
// the order once it says delivered, else the seller's days after shipping do
// (aldoo-na/store/deliveries).
import React, { useState } from "react"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Button from "aldoo-ra/Button"
import { ArrowPathIcon } from "@heroicons/react/24/outline"
import { dateTime } from "./ui"

const inputClass = "w-full px-3 py-2 rounded-md border text-sm outline-none border-admin_bordered bg-admin_paper text-admin_text focus:border-admin_primary"

/** What the courier says of a parcel, as the seller reads it, with its colour. */
export const PARCEL_STATES = {
  pending: { label: "Waiting for the courier", tone: "bg-admin_neutral_1 text-admin_text_secondary" },
  in_transit: { label: "On its way", tone: "bg-admin_info/15 text-admin_info" },
  ready_for_pickup: { label: "Waiting for the buyer to collect it", tone: "bg-admin_warning/20 text-admin_warning" },
  delivered: { label: "Delivered", tone: "bg-admin_success/15 text-admin_success" },
  returning: { label: "Coming back to you", tone: "bg-admin_error/15 text-admin_error" },
  returned: { label: "Delivered back to you", tone: "bg-admin_error/15 text-admin_error" },
  problem: { label: "Problem with the parcel", tone: "bg-admin_error/15 text-admin_error" },
}

// why an order does not complete by itself (deliveries blockerOf), as the seller reads it
const HELD = {
  parcel_returning: "The parcel is coming back to you, so the order does not complete by itself.",
  parcel_returned: "The parcel was delivered back to you, so the order does not complete by itself.",
  parcel_problem: "The courier reports a problem with the parcel, so the order does not complete by itself.",
  return_requested: "A return request is in progress, so the order does not complete by itself.",
  cancel_requested: "A cancellation request is waiting for your answer, so the order does not complete by itself.",
  goods_returned: "Items came back, so the order does not complete by itself.",
  refunded: "The order was refunded in full.",
}

/**
 * What is wrong with a tracking number for the courier picked, or null: the
 * order's courier sends its format (shippingProvider.trackingFormat: { pattern,
 * example, message } — BOX NOW 10 digits, Speedy 10–14), tested without spaces
 * and dashes. Another courier, or a courier without a format, takes anything.
 * The server checks the same.
 */
export function trackingNumberProblem(order, value) {
  const format = value?.carrier && value.carrier === order?.shippingProvider?.providerId ? order?.shippingProvider?.trackingFormat : null
  const number = String(value?.number || "").replace(/[\s-]+/g, "")
  if (!format?.pattern || !number) return null
  try {
    return new RegExp(format.pattern).test(number) ? null : format.message || "This is not a valid tracking number"
  } catch {
    return null
  }
}

/**
 * The fields for a parcel's tracking: the courier (the order's own, or another),
 * the number, and for another courier the link the buyer follows. `onChange`
 * gets { carrier, number, url } on every change.
 */
export function TrackingForm({ order, t, initial = {}, onChange }) {
  const own = order?.shippingProvider?.providerId
  const ownName = order?.shippingProvider?.providerName || own
  const options = [...(own ? [{ id: own, value: own, name: ownName }] : []), { id: "other", value: "other", name: t`Another courier` }]
  const [value, setValue] = useState({ carrier: initial.carrier || own || "other", number: initial.number || "", url: initial.carrier === "other" ? initial.url || "" : "" })
  // the order's courier's number shape: an example to type after, and a word when it does not fit
  const format = value.carrier === own ? order?.shippingProvider?.trackingFormat : null
  const problem = trackingNumberProblem(order, value)
  const update = (patch) => {
    const next = { ...value, ...patch }
    setValue(next)
    onChange?.(next)
  }
  return (
    <div className="flex flex-col gap-3" data-testid="tracking-form">
      {options.length > 1 && (
        <label className="flex flex-col gap-1">
          <span className="text-xs text-admin_text_secondary">{t`Courier`}</span>
          <div data-testid="tracking-carrier">
            <DropdownSelect
              variant="admin"
              options={options}
              value={options.filter((option) => option.id === value.carrier)}
              onChange={(picked) => {
                const first = Array.isArray(picked) ? picked[0] : picked
                if (first) update({ carrier: first.id })
              }}
              searchEnabled={false}
              showCheckbox={false}
              showSelectionTick
              enableResetButton={false}
              allowEmptySelection={false}
            />
          </div>
        </label>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-xs text-admin_text_secondary">{t`Tracking number (required)`}</span>
        <input
          autoFocus
          value={value.number}
          onChange={(e) => update({ number: e.target.value })}
          placeholder={format?.example ? t("e.g. %s", format.example) : t`The number on the waybill`}
          inputMode={format ? "numeric" : undefined}
          className={inputClass}
          data-testid="tracking-number"
        />
        {problem && <span className="text-xs text-admin_error" data-testid="tracking-number-error">{t(problem)}</span>}
      </label>
      {value.carrier === "other" && (
        <label className="flex flex-col gap-1">
          <span className="text-xs text-admin_text_secondary">{t`Tracking link (optional)`}</span>
          <input value={value.url} onChange={(e) => update({ url: e.target.value })} placeholder="https://" className={inputClass} data-testid="tracking-url" />
        </label>
      )}
    </div>
  )
}

/** The Delivery tab's parcel block: the number, the courier's last word and its events, what happens next. */
export function TrackingPanel({ order, t, locale, onEdit, canEdit = true, onShowHistory, onCheck, checking = false }) {
  const tracking = order?.tracking
  const delivery = order?.delivery
  const sent = ["shipping", "completed"].includes(order?.orderStatus)
  if (!sent && !tracking?.number) return null
  const state = tracking?.state ? PARCEL_STATES[tracking.state] : null
  const carrierName = tracking?.carrier === "other" ? t`Another courier` : order?.shippingProvider?.providerId === tracking?.carrier ? order?.shippingProvider?.providerName || tracking?.carrier : tracking?.carrier
  const events = [...(tracking?.events || [])].reverse()
  return (
    <div className="col-span-2 rounded-lg border border-admin_bordered p-3 flex flex-col gap-2" data-testid="order-tracking">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-black/50 uppercase text-[11px] tracking-wide">{t`Tracking`}</span>
        {tracking?.number ? (
          <>
            <span className="font-semibold" data-testid="order-tracking-number">
              {carrierName ? `${carrierName} · ` : ""}
              {tracking.url ? (
                <a href={tracking.url} target="_blank" rel="noreferrer" className="text-admin_primary underline">
                  {tracking.number}
                </a>
              ) : (
                tracking.number
              )}
            </span>
            {state && <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${state.tone}`} data-testid="order-tracking-state">{t(state.label)}</span>}
          </>
        ) : (
          <span className="text-admin_error" data-testid="order-tracking-missing">{t`No tracking number yet`}</span>
        )}
        {/* the courier asked now (what it says of the parcel this minute), and the number changed: together at the right */}
        <div className="ml-auto flex items-center gap-2">
          {onCheck && tracking?.number && order?.orderStatus === "shipping" && (
            <Button variant="admin_secondary" className="min-w-0" onClick={onCheck} disabled={checking} data-testid="order-tracking-check">
              <span className="inline-flex items-center gap-2">
                <ArrowPathIcon className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} />
                {checking ? t`Checking…` : t`Check now`}
              </span>
            </Button>
          )}
          {canEdit && onEdit && sent && (
            <Button variant="admin_secondary" className="min-w-0" onClick={onEdit} data-testid="order-tracking-edit">
              {tracking?.number ? t`Change` : t`Add tracking number`}
            </Button>
          )}
        </div>
      </div>
      {tracking?.text && (
        <div className="text-xs text-black/70">
          {tracking.text}
          {tracking.at ? ` · ${dateTime(tracking.at, locale, t)}` : ""}
        </div>
      )}
      {tracking?.checkedAt && <div className="text-xs text-black/40" data-testid="order-tracking-checked">{t("Last checked with the courier: %s", dateTime(tracking.checkedAt, locale, t))}</div>}
      {tracking?.error && <div className="text-xs text-black/50">{t`The courier could not be reached the last time; it is asked again every few hours.`}</div>}
      {/* what happens next to an order on its way */}
      {order?.orderStatus === "shipping" && delivery && (
        <div className="text-xs text-black/70" data-testid="order-delivery-outlook">
          {delivery.blocker && HELD[delivery.blocker]
            ? t(HELD[delivery.blocker])
            : delivery.courierFollows
              ? t`The order completes when the courier confirms delivery.`
              : delivery.completesAt
                ? t("Unless the courier confirms delivery sooner, the order completes automatically on %s.", new Date(delivery.completesAt).toLocaleDateString(locale, { dateStyle: "medium" }))
                : t`Automatic completion is off in your Delivery settings: complete the order when the buyer has it.`}
        </div>
      )}
      {/* the courier's events are in the order's History, with its own */}
      {events.length > 0 && (onShowHistory ? (
        <button type="button" onClick={onShowHistory} className="self-start text-xs text-admin_primary underline" data-testid="order-tracking-history">{t("The courier's events (%s) are in the order's History", events.length)}</button>
      ) : (
        <div className="text-xs text-black/50">{t("The courier's events (%s) are in the order's History", events.length)}</div>
      ))}
    </div>
  )
}
